import { browser } from '$app/env';
import { goto } from '$app/navigation';
import { resolve } from '$app/paths';
import { page } from '$app/state';
import { addDays } from 'date-fns';
import { locales, type Locale } from '../locales/data';
import { C } from './utils';
import Cookies from 'js-cookie';
import { SvelteDate } from 'svelte/reactivity';
import { Controller } from './ExchangeData';
import type { LayoutRouteId } from '../routes/$types';
import type { RouteId } from '$app/types';

export const asLocale = (value: string | undefined) => {
	if (locales.includes(value as Locale)) {
		return value as Locale;
	}
	return undefined;
};

const withDefaultHandling = (locale: Locale) => {
	return locale == 'en' ? undefined : locale;
};

export const getLocaleCookie = () => {
	if (browser) {
		return asLocale(Cookies.get(C('locale')));
	}
	return undefined;
};

export const getLocaleUserProperty = async () => {
	if (browser) {
		return asLocale((await Controller.get_user_properties(C('locale'))) ?? undefined);
	}
	return undefined;
};

export const setLocaleCookie = (locale: Locale) => {
	if (browser) {
		Cookies.set(C('locale'), locale, { expires: addDays(new SvelteDate(), 7) });
	}
};

export const setLocaleUserProperty = async (locale: Locale) => {
	if (browser) {
		await Controller.set_user_properties(C('locale'), locale);
	}
};

export const selectGetLocaleCookieFn = (route: RouteId | LayoutRouteId) => {
	if (route == '/[[locale=locale]]/plugin') {
		return getLocaleUserProperty;
	}
	return getLocaleCookie;
};

export const selectSetLocaleCookieFn = (route: RouteId | LayoutRouteId) => {
	if (route == '/[[locale=locale]]/plugin') {
		return setLocaleUserProperty;
	}
	return setLocaleCookie;
};

/**
 * Resolves a route using the current locale or explicitly passed params.
 * @param route The route to resolve
 * @param explicitLocale Explicit locale to use
 * @param params Explicit params to use
 * @returns The resolved locale. Aboslute during browser rendering and relative during server rendering.
 */
export const resolveLocale = (
	route: typeof page.route.id,
	explicitLocale?: Locale,
	params?: typeof page.params
) => {
	const passedParamsLocale = params?.locale;
	const currentParamsLocale = page.params.locale;
	const cookieParamsLocale = getLocaleCookie();
	const locale =
		explicitLocale ?? passedParamsLocale ?? currentParamsLocale ?? cookieParamsLocale ?? 'en';
	// @ts-expect-error(Unable to recognize this branch only executes with valid routes)
	return resolve(route, { ...params, locale: withDefaultHandling(locale) });
};

export const resolveLocalePlugin = async (
	route: typeof page.route.id,
	explicitLocale?: Locale,
	params?: typeof page.params
) => {
	const passedParamsLocale = params?.locale;
	const currentParamsLocale = page.params.locale;
	const cookieParamsLocale = await getLocaleUserProperty();
	const locale =
		explicitLocale ?? passedParamsLocale ?? currentParamsLocale ?? cookieParamsLocale ?? 'en';
	// @ts-expect-error(Unable to recognize this branch only executes with valid routes)
	return resolve(route, { ...params, locale: withDefaultHandling(locale) });
};

export const changeLocale = (
	route: typeof page.route.id,
	locale: Locale,
	params?: typeof page.params,
	search = '',
	hash = ''
) => {
	selectSetLocaleCookieFn(page.route.id)(locale);
	return goto(`${resolveLocale(route, locale, params)}${search}${hash}`, { noScroll: true });
};

export const changeLocalePlugin = async (
	route: typeof page.route.id,
	locale: Locale,
	params?: typeof page.params
) => {
	selectSetLocaleCookieFn(page.route.id)(locale);
	return goto(await resolveLocalePlugin(route, locale, params), { noScroll: true });
};

export const useToast = async () => {
	const { toast } = await import('svelte-sonner');
	return toast;
};
